import { Box, Flex, Text } from '@mantine/core';
import { IconFileText, IconFileTextSpark, IconFolder } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Props = {
  fileName: string;
  fileSize?: number;
  subtitle?: string;
  isFolder: boolean;
  isGenerated: boolean;
  variant?: 'default' | 'compact';
  isSelected?: boolean;
};

export default function FileNameWithIcon({
  fileName,
  fileSize,
  subtitle,
  isFolder,
  isGenerated,
  variant = 'default',
  isSelected = false,
}: Props) {
  const secondaryLine = subtitle ?? (fileSize != null ? humanFileSize(fileSize) : null);
  const isCompact = variant === 'compact';

  return (
    <Flex wrap={'nowrap'} gap={isCompact ? 'xxxs' : 'xs'} align="center">
      {!isCompact ? (
        <Flex
          bg="gray.1"
          w={40}
          h={40}
          align="center"
          justify="center"
          style={{ borderRadius: 20 }}
        >
          <FileIcon isFolder={isFolder} isGenerated={isGenerated} />
        </Flex>
      ) : (
        <Flex c={isSelected ? 'brand.6' : 'gray.5'} align="center" style={{ flexShrink: 0 }}>
          <FileIcon isFolder={isFolder} isGenerated={isGenerated} size={16} />
        </Flex>
      )}
      <Box>
        <Text
          fw={FONT_WEIGHTS.medium}
          size={isCompact ? 'textXs' : 'textSm'}
          lh={isCompact ? '18px' : undefined}
          c={isSelected ? 'brand.6' : 'gray.10'}
        >
          {fileName}
        </Text>
        {secondaryLine && (
          <Text fw={500} size="sm" c="gray.7">
            {secondaryLine}
          </Text>
        )}
      </Box>
    </Flex>
  );
}

function FileIcon({
  isFolder,
  isGenerated,
  size = 20,
}: {
  isFolder: boolean;
  isGenerated: boolean;
  size?: number;
}) {
  if (isFolder) {
    return <IconFolder size={size} />;
  }
  if (isGenerated) {
    return <IconFileTextSpark size={size} />;
  }
  return <IconFileText size={size} />;
}

function humanFileSize(size: number | null | undefined) {
  if (!size) {
    return '';
  }
  const i = size == 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
  return +(size / Math.pow(1024, i)).toFixed(2) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
}
